import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Button } from '../components/ui/Button';
import { NotificationContainer } from '../components/ui/Notification';
import { BuildingIcon, DollarSignIcon, CheckIcon, ArrowRightIcon, ArrowLeftIcon, UploadIcon, InfoIcon, MapPinIcon, MailIcon, PhoneIcon, GlobeIcon, CheckCircleIcon } from 'lucide-react';
export function CompanySetup() {
  const navigate = useNavigate();
  // Setup step state
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 3;
  // Form data state
  const [companyInfo, setCompanyInfo] = useState({
    name: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    country: 'United States',
    phone: '',
    email: '',
    website: '',
    taxId: '',
    logo: null,
    logoPreview: null
  });
  const [expenseSettings, setExpenseSettings] = useState({
    currency: 'USD',
    perDiemSource: 'gsa',
    approvalWorkflow: 'manager_only',
    requireReceipts: 'above25',
    expenseCategories: ['meal', 'lodging', 'transportation', 'incidental'],
    submissionDeadline: 30,
    allowFutureDated: false
  });
  // Validation state
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  // Handle company info change
  const handleCompanyInfoChange = e => {
    const {
      name,
      value
    } = e.target;
    setCompanyInfo({
      ...companyInfo,
      [name]: value
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  // Handle expense settings change
  const handleExpenseSettingsChange = e => {
    const {
      name,
      value,
      type,
      checked
    } = e.target;
    setExpenseSettings({
      ...expenseSettings,
      [name]: type === 'checkbox' ? checked : value
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: null
      });
    }
  };
  // Handle logo upload
  const handleLogoUpload = e => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      // Create URL for preview
      const previewUrl = URL.createObjectURL(file);
      setCompanyInfo({
        ...companyInfo,
        logo: file,
        logoPreview: previewUrl
      });
      // Clear error for logo
      if (errors.logo) {
        setErrors({
          ...errors,
          logo: null
        });
      }
    }
  };
  // Handle category toggle
  const toggleCategory = category => {
    if (expenseSettings.expenseCategories.includes(category)) {
      setExpenseSettings({
        ...expenseSettings,
        expenseCategories: expenseSettings.expenseCategories.filter(c => c !== category)
      });
    } else {
      setExpenseSettings({
        ...expenseSettings,
        expenseCategories: [...expenseSettings.expenseCategories, category]
      });
    }
  };
  // Validate company info
  const validateCompanyInfo = () => {
    const newErrors = {};
    if (!companyInfo.name.trim()) newErrors.name = 'Company name is required';
    if (!companyInfo.address.trim()) newErrors.address = 'Address is required';
    if (!companyInfo.city.trim()) newErrors.city = 'City is required';
    if (!companyInfo.state.trim()) newErrors.state = 'State is required';
    if (!companyInfo.zip.trim()) newErrors.zip = 'ZIP/Postal code is required';
    if (!companyInfo.country.trim()) newErrors.country = 'Country is required';
    if (!companyInfo.phone.trim()) newErrors.phone = 'Phone number is required';
    if (!companyInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(companyInfo.email)) {
      newErrors.email = 'Email is invalid';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Validate expense settings
  const validateExpenseSettings = () => {
    const newErrors = {};
    if (!expenseSettings.currency) newErrors.currency = 'Currency is required';
    if (!expenseSettings.perDiemSource) newErrors.perDiemSource = 'Per diem source is required';
    if (!expenseSettings.approvalWorkflow) newErrors.approvalWorkflow = 'Approval workflow is required';
    if (!expenseSettings.requireReceipts) newErrors.requireReceipts = 'Receipt requirement is required';
    if (expenseSettings.expenseCategories.length === 0) newErrors.expenseCategories = 'At least one expense category is required';
    if (!expenseSettings.submissionDeadline) {
      newErrors.submissionDeadline = 'Submission deadline is required';
    } else if (isNaN(parseInt(expenseSettings.submissionDeadline)) || parseInt(expenseSettings.submissionDeadline) <= 0) {
      newErrors.submissionDeadline = 'Submission deadline must be a positive number';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  // Handle next step
  const handleNextStep = () => {
    if (currentStep === 1) {
      if (!validateCompanyInfo()) return;
    } else if (currentStep === 2) {
      if (!validateExpenseSettings()) return;
    }
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };
  // Handle previous step
  const handlePreviousStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };
  // Handle form submission
  const handleSubmit = e => {
    e.preventDefault();
    // Final validation
    if (currentStep === 1 && !validateCompanyInfo()) return;
    if (currentStep === 2 && !validateExpenseSettings()) return;
    setIsSubmitting(true);
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      // Add success notification
      addNotification({
        type: 'success',
        title: 'Setup Complete',
        message: 'Your company has been set up successfully. You can now start using the expense tracking system.'
      });
      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/admin');
      }, 2000);
    }, 1500);
  };
  // Add notification
  const addNotification = notification => {
    const id = Date.now();
    setNotifications(prev => [...prev, {
      id,
      ...notification
    }]);
  };
  // Remove notification
  const removeNotification = id => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };
  // Render step 1 - Company Information
  const renderCompanyInfoStep = () => {
    return <div className="space-y-6" data-id="element-1105">
        <div data-id="element-1106">
          <h2 className="text-xl font-bold text-gray-100" data-id="element-1107">
            Company Information
          </h2>
          <p className="text-gray-400 mt-1" data-id="element-1108">
            Let's start by setting up your company profile
          </p>
        </div>
        <Card className="p-6" data-id="element-1109">
          <div className="space-y-6" data-id="element-1110">
            <div data-id="element-1111">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1112">
                Company Logo
              </label>
              <div className="mt-1 flex items-center" data-id="element-1113">
                {companyInfo.logoPreview ? <div className="h-20 w-20 rounded-md border border-gray-600 bg-gray-700 flex items-center justify-center overflow-hidden" data-id="element-1114">
                    <img src={companyInfo.logoPreview} alt="Company logo" className="h-full w-full object-contain" data-id="element-1115" />
                  </div> : <div className="h-20 w-20 rounded-md border border-gray-600 bg-gray-700 flex items-center justify-center text-gray-400" data-id="element-1116">
                    <BuildingIcon size={32} data-id="element-1117" />
                  </div>}
                <label className="ml-5 bg-gray-700 border border-gray-600 py-2 px-3 rounded-md shadow-sm text-sm leading-4 font-medium text-gray-200 hover:bg-gray-600 hover:text-gray-100 focus:outline-none cursor-pointer" data-id="element-1118">
                  {companyInfo.logo ? 'Change Logo' : 'Upload Logo'}
                  <input type="file" className="sr-only" accept="image/*" onChange={handleLogoUpload} data-id="element-1119" />
                </label>
              </div>
              <p className="mt-1 text-xs text-gray-400" data-id="element-1120">
                Recommended size: 200x200 pixels (PNG or JPG)
              </p>
            </div>
            <Input label="Company Name" name="name" value={companyInfo.name} onChange={handleCompanyInfoChange} error={errors.name} required data-id="element-1121" />
            <div data-id="element-1122">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1123">
                Street Address <span className="text-red-500" data-id="element-1124">*</span>
              </label>
              <div className="relative" data-id="element-1125">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1126">
                  <MapPinIcon size={16} className="text-gray-400" data-id="element-1127" />
                </div>
                <input type="text" name="address" value={companyInfo.address} onChange={handleCompanyInfoChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${errors.address ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} placeholder="123 Business St" required data-id="element-1128" />
              </div>
              {errors.address && <p className="mt-1 text-sm text-red-500" data-id="element-1129">{errors.address}</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1130">
              <div data-id="element-1131">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1132">
                  City <span className="text-red-500" data-id="element-1133">*</span>
                </label>
                <input type="text" name="city" value={companyInfo.city} onChange={handleCompanyInfoChange} className={`w-full px-3 py-2 bg-gray-700 border ${errors.city ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} placeholder="San Francisco" required data-id="element-1134" />
                {errors.city && <p className="mt-1 text-sm text-red-500" data-id="element-1135">{errors.city}</p>}
              </div>
              <div data-id="element-1136">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1137">
                  State/Province <span className="text-red-500" data-id="element-1138">*</span>
                </label>
                <input type="text" name="state" value={companyInfo.state} onChange={handleCompanyInfoChange} className={`w-full px-3 py-2 bg-gray-700 border ${errors.state ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} placeholder="CA" required data-id="element-1139" />
                {errors.state && <p className="mt-1 text-sm text-red-500" data-id="element-1140">{errors.state}</p>}
              </div>
              <div data-id="element-1141">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1142">
                  ZIP/Postal Code <span className="text-red-500" data-id="element-1143">*</span>
                </label>
                <input type="text" name="zip" value={companyInfo.zip} onChange={handleCompanyInfoChange} className={`w-full px-3 py-2 bg-gray-700 border ${errors.zip ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} placeholder="94105" required data-id="element-1144" />
                {errors.zip && <p className="mt-1 text-sm text-red-500" data-id="element-1145">{errors.zip}</p>}
              </div>
            </div>
            <div data-id="element-1146">
              <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1147">
                Country <span className="text-red-500" data-id="element-1148">*</span>
              </label>
              <select name="country" value={companyInfo.country} onChange={handleCompanyInfoChange} className={`w-full px-3 py-2 bg-gray-700 border ${errors.country ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} required data-id="element-1149">
                <option value="United States" data-id="element-1150">United States</option>
                <option value="Canada" data-id="element-1151">Canada</option>
                <option value="United Kingdom" data-id="element-1152">United Kingdom</option>
                <option value="Australia" data-id="element-1153">Australia</option>
                <option value="Germany" data-id="element-1154">Germany</option>
                <option value="France" data-id="element-1155">France</option>
                <option value="Japan" data-id="element-1156">Japan</option>
                <option value="Other" data-id="element-1157">Other</option>
              </select>
              {errors.country && <p className="mt-1 text-sm text-red-500" data-id="element-1158">{errors.country}</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1159">
              <div data-id="element-1160">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1161">
                  Phone Number <span className="text-red-500" data-id="element-1162">*</span>
                </label>
                <div className="relative" data-id="element-1163">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1164">
                    <PhoneIcon size={16} className="text-gray-400" data-id="element-1165" />
                  </div>
                  <input type="text" name="phone" value={companyInfo.phone} onChange={handleCompanyInfoChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${errors.phone ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} placeholder="(555) 123-4567" required data-id="element-1166" />
                </div>
                {errors.phone && <p className="mt-1 text-sm text-red-500" data-id="element-1167">{errors.phone}</p>}
              </div>
              <div data-id="element-1168">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1169">
                  Email Address <span className="text-red-500" data-id="element-1170">*</span>
                </label>
                <div className="relative" data-id="element-1171">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1172">
                    <MailIcon size={16} className="text-gray-400" data-id="element-1173" />
                  </div>
                  <input type="email" name="email" value={companyInfo.email} onChange={handleCompanyInfoChange} className={`pl-10 w-full px-3 py-2 bg-gray-700 border ${errors.email ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} placeholder="info@yourcompany.com" required data-id="element-1174" />
                </div>
                {errors.email && <p className="mt-1 text-sm text-red-500" data-id="element-1175">{errors.email}</p>}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1176">
              <div data-id="element-1177">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1178">
                  Website
                </label>
                <div className="relative" data-id="element-1179">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1180">
                    <GlobeIcon size={16} className="text-gray-400" data-id="element-1181" />
                  </div>
                  <input type="text" name="website" value={companyInfo.website} onChange={handleCompanyInfoChange} className="pl-10 w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" placeholder="https://www.yourcompany.com" data-id="element-1182" />
                </div>
              </div>
              <div data-id="element-1183">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1184">
                  Tax ID / EIN
                </label>
                <input type="text" name="taxId" value={companyInfo.taxId} onChange={handleCompanyInfoChange} className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200" placeholder="12-3456789" data-id="element-1185" />
              </div>
            </div>
          </div>
        </Card>
      </div>;
  };
  // Render step 2 - Expense Settings
  const renderExpenseSettingsStep = () => {
    return <div className="space-y-6" data-id="element-1186">
        <div data-id="element-1187">
          <h2 className="text-xl font-bold text-gray-100" data-id="element-1188">Expense Settings</h2>
          <p className="text-gray-400 mt-1" data-id="element-1189">
            Configure expense policies and approval workflows
          </p>
        </div>
        <Card className="p-6" data-id="element-1190">
          <div className="space-y-6" data-id="element-1191">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1192">
              <div data-id="element-1193">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1194">
                  Default Currency <span className="text-red-500" data-id="element-1195">*</span>
                </label>
                <select name="currency" value={expenseSettings.currency} onChange={handleExpenseSettingsChange} className={`w-full px-3 py-2 bg-gray-700 border ${errors.currency ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} required data-id="element-1196">
                  <option value="USD" data-id="element-1197">USD - US Dollar</option>
                  <option value="EUR" data-id="element-1198">EUR - Euro</option>
                  <option value="GBP" data-id="element-1199">GBP - British Pound</option>
                  <option value="CAD" data-id="element-1200">CAD - Canadian Dollar</option>
                  <option value="AUD" data-id="element-1201">AUD - Australian Dollar</option>
                  <option value="JPY" data-id="element-1202">JPY - Japanese Yen</option>
                </select>
                {errors.currency && <p className="mt-1 text-sm text-red-500" data-id="element-1203">{errors.currency}</p>}
              </div>
              <div data-id="element-1204">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1205">
                  Per Diem Rate Source <span className="text-red-500" data-id="element-1206">*</span>
                </label>
                <select name="perDiemSource" value={expenseSettings.perDiemSource} onChange={handleExpenseSettingsChange} className={`w-full px-3 py-2 bg-gray-700 border ${errors.perDiemSource ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} required data-id="element-1207">
                  <option value="gsa" data-id="element-1208">
                    GSA (U.S. General Services Administration)
                  </option>
                  <option value="state" data-id="element-1209">U.S. State Department</option>
                  <option value="custom" data-id="element-1210">Custom Rates</option>
                  <option value="none" data-id="element-1211">No Per Diem Enforcement</option>
                </select>
                {errors.perDiemSource && <p className="mt-1 text-sm text-red-500" data-id="element-1212">
                    {errors.perDiemSource}
                  </p>}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1213">
              <div data-id="element-1214">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1215">
                  Approval Workflow <span className="text-red-500" data-id="element-1216">*</span>
                </label>
                <select name="approvalWorkflow" value={expenseSettings.approvalWorkflow} onChange={handleExpenseSettingsChange} className={`w-full px-3 py-2 bg-gray-700 border ${errors.approvalWorkflow ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} required data-id="element-1217">
                  <option value="manager_only" data-id="element-1218">Manager Approval Only</option>
                  <option value="manager_finance" data-id="element-1219">Manager then Finance</option>
                  <option value="amount_based" data-id="element-1220">
                    Based on Amount Thresholds
                  </option>
                  <option value="auto_approve" data-id="element-1221">
                    Auto-Approve (No Workflow)
                  </option>
                </select>
                {errors.approvalWorkflow && <p className="mt-1 text-sm text-red-500" data-id="element-1222">
                    {errors.approvalWorkflow}
                  </p>}
              </div>
              <div data-id="element-1223">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1224">
                  Receipt Requirements <span className="text-red-500" data-id="element-1225">*</span>
                </label>
                <select name="requireReceipts" value={expenseSettings.requireReceipts} onChange={handleExpenseSettingsChange} className={`w-full px-3 py-2 bg-gray-700 border ${errors.requireReceipts ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} required data-id="element-1226">
                  <option value="all" data-id="element-1227">Required for All Expenses</option>
                  <option value="above25" data-id="element-1228">
                    Required for Expenses Above $25
                  </option>
                  <option value="above75" data-id="element-1229">
                    Required for Expenses Above $75
                  </option>
                  <option value="none" data-id="element-1230">Not Required</option>
                </select>
                {errors.requireReceipts && <p className="mt-1 text-sm text-red-500" data-id="element-1231">
                    {errors.requireReceipts}
                  </p>}
              </div>
            </div>
            <div data-id="element-1232">
              <label className="block text-sm font-medium text-gray-200 mb-3" data-id="element-1233">
                Expense Categories <span className="text-red-500" data-id="element-1234">*</span>
              </label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-id="element-1235">
                {['meal', 'lodging', 'transportation', 'incidental', 'office', 'software', 'entertainment', 'professional'].map(category => <div key={category} className={`flex items-center p-3 rounded-md border ${expenseSettings.expenseCategories.includes(category) ? 'border-blue-500 bg-blue-900/20' : 'border-gray-600 bg-gray-700'} cursor-pointer`} onClick={() => toggleCategory(category)} data-id="element-1236">
                    <input type="checkbox" checked={expenseSettings.expenseCategories.includes(category)} onChange={() => {}} className="h-4 w-4 text-blue-600 rounded border-gray-500 focus:ring-blue-500 bg-gray-700" data-id="element-1237" />
                    <span className="ml-2 text-sm text-gray-200 capitalize" data-id="element-1238">
                      {category}
                    </span>
                  </div>)}
              </div>
              {errors.expenseCategories && <p className="mt-1 text-sm text-red-500" data-id="element-1239">
                  {errors.expenseCategories}
                </p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1240">
              <div data-id="element-1241">
                <label className="block text-sm font-medium text-gray-200 mb-1" data-id="element-1242">
                  Submission Deadline (Days){' '}
                  <span className="text-red-500" data-id="element-1243">*</span>
                </label>
                <input type="number" name="submissionDeadline" value={expenseSettings.submissionDeadline} onChange={handleExpenseSettingsChange} min="1" max="90" className={`w-full px-3 py-2 bg-gray-700 border ${errors.submissionDeadline ? 'border-red-500' : 'border-gray-600'} rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-200`} required data-id="element-1244" />
                <p className="mt-1 text-xs text-gray-400" data-id="element-1245">
                  Number of days after expense date that submissions are allowed
                </p>
                {errors.submissionDeadline && <p className="mt-1 text-sm text-red-500" data-id="element-1246">
                    {errors.submissionDeadline}
                  </p>}
              </div>
              <div className="flex items-center" data-id="element-1247">
                <input type="checkbox" id="allowFutureDated" name="allowFutureDated" checked={expenseSettings.allowFutureDated} onChange={handleExpenseSettingsChange} className="h-4 w-4 text-blue-600 rounded border-gray-500 focus:ring-blue-500 bg-gray-700" data-id="element-1248" />
                <label htmlFor="allowFutureDated" className="ml-2 text-sm text-gray-200" data-id="element-1249">
                  Allow future-dated expenses
                </label>
              </div>
            </div>
            <div className="bg-blue-900/30 border border-blue-800 rounded-md p-4 flex items-start" data-id="element-1250">
              <InfoIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-1251" />
              <div data-id="element-1252">
                <h3 className="text-sm font-medium text-blue-300" data-id="element-1253">
                  About Per Diem Rates
                </h3>
                <p className="text-sm text-blue-200 mt-1" data-id="element-1254">
                  GSA per diem rates are used by federal employees for travel
                  within the continental United States. These rates include
                  separate allowances for lodging, meals, and incidental
                  expenses and vary by location and time of year.
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>;
  };
  // Render step 3 - Review & Submit
  const renderReviewStep = () => {
    return <div className="space-y-6" data-id="element-1255">
        <div data-id="element-1256">
          <h2 className="text-xl font-bold text-gray-100" data-id="element-1257">Review & Submit</h2>
          <p className="text-gray-400 mt-1" data-id="element-1258">
            Review your company setup details before submitting
          </p>
        </div>
        <Card className="p-6" data-id="element-1259">
          <div className="space-y-6" data-id="element-1260">
            <div className="flex justify-between items-start" data-id="element-1261">
              <div data-id="element-1262">
                <h3 className="text-lg font-medium text-gray-100" data-id="element-1263">
                  Company Information
                </h3>
                <p className="text-sm text-gray-400 mt-1" data-id="element-1264">
                  Basic details about your organization
                </p>
              </div>
              <button type="button" onClick={() => setCurrentStep(1)} className="text-blue-400 hover:text-blue-300 text-sm flex items-center" data-id="element-1265">
                Edit
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 pt-2" data-id="element-1266">
              <div className="flex" data-id="element-1267">
                <div className="w-32 text-sm text-gray-400" data-id="element-1268">Company Name:</div>
                <div className="text-sm font-medium text-gray-200" data-id="element-1269">
                  {companyInfo.name || 'Not provided'}
                </div>
              </div>
              <div className="flex" data-id="element-1270">
                <div className="w-32 text-sm text-gray-400" data-id="element-1271">Email:</div>
                <div className="text-sm font-medium text-gray-200" data-id="element-1272">
                  {companyInfo.email || 'Not provided'}
                </div>
              </div>
              <div className="flex" data-id="element-1273">
                <div className="w-32 text-sm text-gray-400" data-id="element-1274">Phone:</div>
                <div className="text-sm font-medium text-gray-200" data-id="element-1275">
                  {companyInfo.phone || 'Not provided'}
                </div>
              </div>
              <div className="flex" data-id="element-1276">
                <div className="w-32 text-sm text-gray-400" data-id="element-1277">Website:</div>
                <div className="text-sm font-medium text-gray-200" data-id="element-1278">
                  {companyInfo.website || 'Not provided'}
                </div>
              </div>
              <div className="flex" data-id="element-1279">
                <div className="w-32 text-sm text-gray-400" data-id="element-1280">Address:</div>
                <div className="text-sm font-medium text-gray-200" data-id="element-1281">
                  {companyInfo.address ? <>
                      {companyInfo.address}, {companyInfo.city},{' '}
                      {companyInfo.state} {companyInfo.zip},{' '}
                      {companyInfo.country}
                    </> : 'Not provided'}
                </div>
              </div>
              <div className="flex" data-id="element-1282">
                <div className="w-32 text-sm text-gray-400" data-id="element-1283">Tax ID:</div>
                <div className="text-sm font-medium text-gray-200" data-id="element-1284">
                  {companyInfo.taxId || 'Not provided'}
                </div>
              </div>
            </div>
            <div className="border-t border-gray-700 pt-6 mt-6" data-id="element-1285">
              <div className="flex justify-between items-start" data-id="element-1286">
                <div data-id="element-1287">
                  <h3 className="text-lg font-medium text-gray-100" data-id="element-1288">
                    Expense Settings
                  </h3>
                  <p className="text-sm text-gray-400 mt-1" data-id="element-1289">
                    Policy configurations for expense management
                  </p>
                </div>
                <button type="button" onClick={() => setCurrentStep(2)} className="text-blue-400 hover:text-blue-300 text-sm flex items-center" data-id="element-1290">
                  Edit
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4 pt-2" data-id="element-1291">
                <div className="flex" data-id="element-1292">
                  <div className="w-32 text-sm text-gray-400" data-id="element-1293">Currency:</div>
                  <div className="text-sm font-medium text-gray-200" data-id="element-1294">
                    {expenseSettings.currency}
                  </div>
                </div>
                <div className="flex" data-id="element-1295">
                  <div className="w-32 text-sm text-gray-400" data-id="element-1296">
                    Per Diem Source:
                  </div>
                  <div className="text-sm font-medium text-gray-200" data-id="element-1297">
                    {expenseSettings.perDiemSource === 'gsa' && 'GSA (U.S. General Services Administration)'}
                    {expenseSettings.perDiemSource === 'state' && 'U.S. State Department'}
                    {expenseSettings.perDiemSource === 'custom' && 'Custom Rates'}
                    {expenseSettings.perDiemSource === 'none' && 'No Per Diem Enforcement'}
                  </div>
                </div>
                <div className="flex" data-id="element-1298">
                  <div className="w-32 text-sm text-gray-400" data-id="element-1299">
                    Approval Flow:
                  </div>
                  <div className="text-sm font-medium text-gray-200" data-id="element-1300">
                    {expenseSettings.approvalWorkflow === 'manager_only' && 'Manager Approval Only'}
                    {expenseSettings.approvalWorkflow === 'manager_finance' && 'Manager then Finance'}
                    {expenseSettings.approvalWorkflow === 'amount_based' && 'Based on Amount Thresholds'}
                    {expenseSettings.approvalWorkflow === 'auto_approve' && 'Auto-Approve (No Workflow)'}
                  </div>
                </div>
                <div className="flex" data-id="element-1301">
                  <div className="w-32 text-sm text-gray-400" data-id="element-1302">Receipts:</div>
                  <div className="text-sm font-medium text-gray-200" data-id="element-1303">
                    {expenseSettings.requireReceipts === 'all' && 'Required for All Expenses'}
                    {expenseSettings.requireReceipts === 'above25' && 'Required for Expenses Above $25'}
                    {expenseSettings.requireReceipts === 'above75' && 'Required for Expenses Above $75'}
                    {expenseSettings.requireReceipts === 'none' && 'Not Required'}
                  </div>
                </div>
                <div className="flex" data-id="element-1304">
                  <div className="w-32 text-sm text-gray-400" data-id="element-1305">Categories:</div>
                  <div className="text-sm font-medium text-gray-200" data-id="element-1306">
                    {expenseSettings.expenseCategories.map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(', ')}
                  </div>
                </div>
                <div className="flex" data-id="element-1307">
                  <div className="w-32 text-sm text-gray-400" data-id="element-1308">Deadline:</div>
                  <div className="text-sm font-medium text-gray-200" data-id="element-1309">
                    {expenseSettings.submissionDeadline} days
                  </div>
                </div>
                <div className="flex" data-id="element-1310">
                  <div className="w-32 text-sm text-gray-400" data-id="element-1311">
                    Future Expenses:
                  </div>
                  <div className="text-sm font-medium text-gray-200" data-id="element-1312">
                    {expenseSettings.allowFutureDated ? 'Allowed' : 'Not Allowed'}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-green-900/30 border border-green-800 rounded-md p-4 flex items-start mt-6" data-id="element-1313">
              <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 mr-3 flex-shrink-0" data-id="element-1314" />
              <div data-id="element-1315">
                <h3 className="text-sm font-medium text-green-300" data-id="element-1316">
                  Ready to Launch
                </h3>
                <p className="text-sm text-green-200 mt-1" data-id="element-1317">
                  Your expense management system is now configured and ready to
                  use. You can always modify these settings later from the admin
                  dashboard.
                </p>
              </div>
            </div>
          </div>
        </Card>
      </div>;
  };
  // Render step content based on current step
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderCompanyInfoStep();
      case 2:
        return renderExpenseSettingsStep();
      case 3:
        return renderReviewStep();
      default:
        return renderCompanyInfoStep();
    }
  };
  return <div className="max-w-4xl mx-auto" data-id="element-1318">
      {/* Notifications */}
      <NotificationContainer notifications={notifications} onClose={removeNotification} data-id="element-1319" />
      <div className="mb-8" data-id="element-1320">
        <h1 className="text-2xl font-bold text-gray-100" data-id="element-1321">Company Setup</h1>
        <p className="text-gray-400 mt-1" data-id="element-1322">
          Set up your company profile and expense management policies
        </p>
      </div>
      {/* Progress Steps */}
      <div className="mb-8" data-id="element-1323">
        <div className="flex items-center" data-id="element-1324">
          {Array.from({
          length: totalSteps
        }).map((_, index) => <Fragment key={index} data-id="element-1325">
              {index > 0 && <div className={`flex-1 h-1 ${index < currentStep ? 'bg-blue-500' : 'bg-gray-600'}`} data-id="element-1326"></div>}
              <div className="relative" data-id="element-1327">
                <div className={`h-8 w-8 rounded-full flex items-center justify-center ${index + 1 === currentStep ? 'bg-blue-600 text-white border-2 border-blue-400' : index + 1 < currentStep ? 'bg-blue-700 text-white' : 'bg-gray-700 text-gray-400 border border-gray-600'}`} data-id="element-1328">
                  {index + 1 < currentStep ? <CheckIcon size={16} data-id="element-1329" /> : <span className="text-sm" data-id="element-1330">{index + 1}</span>}
                </div>
                <span className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-xs text-gray-400 whitespace-nowrap" data-id="element-1331">
                  {index === 0 && 'Company Info'}
                  {index === 1 && 'Expense Settings'}
                  {index === 2 && 'Review & Submit'}
                </span>
              </div>
            </Fragment>)}
        </div>
      </div>
      <form onSubmit={handleSubmit} data-id="element-1332">
        {/* Step Content */}
        {renderStepContent()}
        {/* Navigation Buttons */}
        <div className="mt-8 flex justify-between" data-id="element-1333">
          {currentStep > 1 ? <Button type="button" variant="outline" onClick={handlePreviousStep} data-id="element-1334">
              <ArrowLeftIcon size={16} className="mr-1.5" data-id="element-1335" />
              Previous Step
            </Button> : <div data-id="element-1336"></div>}
          {currentStep < totalSteps ? <Button type="button" onClick={handleNextStep} data-id="element-1337">
              Next Step
              <ArrowRightIcon size={16} className="ml-1.5" data-id="element-1338" />
            </Button> : <Button type="submit" disabled={isSubmitting} data-id="element-1339">
              {isSubmitting ? <>
                  <div className="animate-spin mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full" data-id="element-1340"></div>
                  Setting Up...
                </> : <>
                  <CheckIcon size={16} className="mr-1.5" data-id="element-1341" />
                  Complete Setup
                </>}
            </Button>}
        </div>
      </form>
    </div>;
}